import { AWARD_STATUS, FILENAMES, STEPS, SUBMIT_GRACE_TIME_MINUTES } from './constants';
import { ICourse } from './ICourse';
import { ICourseModule } from './ICourseModule';
import { ICourseRoles } from './ICourseRoles';
import { IProvider } from './IProvider';
import { IResultsSource } from './IResultsSource';
import { IWorkflowAction } from './IWorkflowAction';
import { getMomentDate } from './parse';
import { WorkflowTextService } from './WorkflowTextService';

export class WorkflowServiceV1 {

  public static getCourseSteps() {
    return [STEPS.STEP1, STEPS.STEP2, STEPS.STEP3, STEPS.REVIEW, STEPS.COMPLETED];
  }

  public static getNextStatus(moduleId: string, action: string | undefined, results: { [key: string]: any }): string {

    switch (action) {
      case 'edit':
        switch (moduleId) {
          default:
            return `${moduleId} | submitted`;
          case STEPS.REVIEW:
            return `${STEPS.REVIEW} | committee`;
        }


      case 'review':
        switch (moduleId) {

          case STEPS.STEP1:
            if (results.ApprovalSTEP1 === 'approved') {
              return `${STEPS.STEP1} | approved`;
            } else if (results.ApprovalSTEP1 === 'minor_changes' || results.ApprovalSTEP1 === 'major_changes') {
              return `${STEPS.STEP1} | rejected`;
            }
            break;

          case STEPS.STEP2:
            if (results.ApprovalSTEP2 === 'approved') {
              return `${STEPS.STEP2} | approved`;
            } else if (results.ApprovalSTEP2 === 'rejected') {
              return `${STEPS.STEP2} | rejected`;
            }
            break;

          case STEPS.STEP3:
            if (results.ApprovalSTEP3 === 'approved') {
              return `${STEPS.REVIEW} | committee`;
            } else if (results.ApprovalSTEP3 === 'expert') {
              return `${STEPS.REVIEW} | expert`;
            } else if (results.ApprovalSTEP3 === 'rejected') {
              return `${STEPS.STEP3} | rejected`;
            }
            break;

          case STEPS.REVIEW:
            return `COMPLETED`;
        }
        break;
    }
    
    return '';
  }

  static isStepCompleted(moduleId: string, courseStatus: string): boolean {

    switch (moduleId) {
      case STEPS.STEP1:
        return courseStatus.startsWith(STEPS.STEP2) || courseStatus.startsWith(STEPS.STEP3) || courseStatus.startsWith(STEPS.REVIEW) || courseStatus.startsWith('COMPLETED');
      case STEPS.STEP2:
        return courseStatus.startsWith(STEPS.STEP3) || courseStatus.startsWith(STEPS.REVIEW) || courseStatus?.startsWith('COMPLETED');
      case STEPS.STEP3:
        return courseStatus.startsWith(STEPS.REVIEW) || courseStatus.startsWith('COMPLETED');
      case STEPS.REVIEW:
        return courseStatus.startsWith('COMPLETED');
      default:
        return false;
    }
  }

  public static getWorkflowModifyAction = (roles: ICourseRoles, moduleId: string, course: ICourse, isCreated?: boolean): IWorkflowAction | undefined => {

    const courseStep = course.CourseStep;

    switch (moduleId) {
      case STEPS.STEP1:
        if (roles.isStaff && !roles.isAdmin) {
          return;
        }
        if (!courseStep || courseStep === `${STEPS.STEP1} | draft`) {
          return {
            id: isCreated ? 'edit' : 'new',
            title: isCreated ? "Continue Step 1" : "Start Step 1",
            tooltip: isCreated ? "Continue the Step 1 questionnaire" : "Start the Step 1 questionnaire"
          }
        } else {
          break;
        }
      case STEPS.STEP2:
        if (roles.isStaff && !roles.isAdmin) {
          return;
        }
        if (courseStep === `${STEPS.STEP2} | draft` || courseStep === `${STEPS.STEP1} | approved`) {
          return {
            id: isCreated ? 'edit' : 'new',
            title: isCreated ? "Continue Step 2" : "Start Step 2",
            tooltip: isCreated ? "Continue the Step 2 questionnaire" : "Start the Step 2 questionnaire"
          }
        } else {
          break;
        }
      case STEPS.STEP3:
        if (roles.isStaff && !roles.isAdmin) {
          break;
        }
        if (courseStep === `${STEPS.STEP3} | draft` || courseStep === `${STEPS.STEP2} | approved`) {
          return {
            id: isCreated ? 'edit' : 'new',
            title: isCreated ? "Continue Step 3" : "Start Step 3",
            tooltip: isCreated ? "Continue the Step 3 questionnaire" : "Start the Step 3 questionnaire"
          }
        } else {
          break;
        }
      case STEPS.REVIEW:
        if (!roles.isExternalExpert) {
          return;
        }
        if (courseStep === `${STEPS.REVIEW} | expert`) {
          return {
            id: isCreated ? 'edit' : 'new',
            title: isCreated ? "Continue Course Review" : "Start Course Review",
            tooltip: isCreated ? "Continue the Course Review" : "Start the Course Review"
          }
        } else {
          break;
        }
    }

    if (!roles.isStaff && !roles.isAdmin) {
      const dateModified = getMomentDate(course.modified);
      const dateNow = getMomentDate(new Date());
      if (dateModified && dateNow && courseStep.startsWith(moduleId) && courseStep.endsWith('submitted')) {
        const diff = dateNow.diff(dateModified, 'minutes');
        if (diff < SUBMIT_GRACE_TIME_MINUTES)
          return {
            id: 'edit',
            title: `Resubmit`,
            tooltip: `You can modify and resubmit the questionnaire within ${SUBMIT_GRACE_TIME_MINUTES - diff} minutes.`
          }
      }
    }

    if (courseStep.startsWith(moduleId)) {
      if (courseStep.endsWith('rejected')) {
        return {
          id: 'edit',
          title: `Correct`,
          tooltip: `Correct and resubmit the questionnaire.`
        }
      }
    }
  }

  public static getWorkflowReviewAction = (roles: ICourseRoles, moduleId: string, course: ICourse, isCreated?: boolean): IWorkflowAction | undefined => {

    const courseStatus = course?.CourseStep;

    if (roles.isStaff && courseStatus === `${STEPS.REVIEW} | committee` && moduleId === STEPS.REVIEW) {
      return {
        id: 'review',
        title: `Finalize`,
        tooltip: `Finalize the submission.`
      }
    };

    if (!courseStatus || !isCreated) {
      return;
    }

    const moduleName = WorkflowTextService.getModuleName(moduleId);

    if (roles.isStaff && courseStatus.startsWith(moduleId)) {
      if (courseStatus.endsWith('draft'))
        return;
      if (courseStatus.endsWith('submitted')) {
        return {
          id: 'review',
          title: `Review ${moduleName}`,
          tooltip: `Review and provide feedback for the ${moduleName} questionnaire.`
        }
      }
      if (courseStatus.endsWith('approved')) {
        return {
          id: 'review',
          title: `Review ${moduleName} Again`,
          tooltip: `Review and provide feedback for the ${moduleName} questionnaire.`
        }
      }
      if (courseStatus.endsWith('rejected')) {
        return {
          id: 'review',
          title: `Review ${moduleName} Again`,
          tooltip: `Review and provide feedback for the ${moduleName} questionnaire.`
        }
      }
    };
  }

  public static getStepDescription(course: ICourse, roles: ICourseRoles, moduleId: string): string {
    switch (moduleId) {
      case STEPS.STEP1:
        return "Pre-self assessment for EIT Label application";
      case STEPS.STEP2:
        return "Course Development and Implementation";
      case STEPS.STEP3:
        return "Outcome and Final Assessment";
        case STEPS.REVIEW:
        return `Course Review by External Reviewer ${WorkflowTextService.formatExpertLink(course.expert, roles)}`;
      default:
        return '';
    };
  }

  public static getDetailedStepDescription(course: ICourse, roles: ICourseRoles) {
    switch (course?.CourseStep) {
      case `${STEPS.STEP1} | draft`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to submit the Step 1 questionnaire.", color: 'yellow' }
          : { text: "Please submit the Step 1 questionnaire to continue the application process.", color: 'brand' };
      case `${STEPS.STEP1} | submitted`:
        return roles.isStaff
          ? { text: "Please review the Step 1 questionnaire and provide the feedback.", color: 'blue' }
          : { text: "Waiting for the EIT UM staff to review the Step 1 questionnaire and provide feedback.", color: 'yellow' };
      case `${STEPS.STEP1} | approved`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to start the Step 2 questionnaire.", color: 'yellow' }
          : { text: "The Step 1 questionnaire has been approved. You can start the Step 2 questionnaire.", color: 'blue' }
      case `${STEPS.STEP1} | rejected`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to correct the Step 1 questionnaire and submit it again.", color: 'yellow' }
          : { text: "The Step 1 questionnaire needs some correction. Please correct it and submit it again.", color: 'blue' };

      case `${STEPS.STEP2} | draft`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to submit the Step 2 questionnaire.", color: 'yellow' }
          : { text: "Please submit the Step 2 questionnaire to continue the application process.", color: 'brand' };
      case `${STEPS.STEP2} | submitted`:
        return roles.isStaff
          ? { text: "Please review the Step 2 questionnaire and provide the feedback.", color: 'blue' }
          : { text: "Waiting for the EIT UM staff to review the Step 2 questionnaire and provide feedback.", color: 'yellow' };
      case `${STEPS.STEP2} | approved`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to start the Step 3 questionnaire.", color: 'yellow' }
          : { text: "The Step 2 questionnaire has been approved. You can start the Step 3 questionnaire.", color: 'blue' }
      case `${STEPS.STEP2} | rejected`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to correct the Step 2 questionnaire and submit it again.", color: 'yellow' }
          : { text: "The Step 2 questionnaire needs some correction. Please correct it and submit it again.", color: 'blue' };

      case `${STEPS.STEP3} | draft`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to submit the Step 3 questionnaire.", color: 'yellow' }
          : { text: "Please submit the Step 3 questionnaire to continue the application process.", color: 'brand' }
      case `${STEPS.STEP3} | submitted`:
        return roles.isStaff
          ? { text: "Please review the Step 3 questionnaire and provide the feedback.", color: 'blue' }
          : { text: "Waiting for the EIT UM staff to review the Step 3 questionnaire and provide feedback.", color: 'yellow' }
      case `${STEPS.STEP3} | rejected`:
        return roles.isStaff
          ? { text: "Waiting for the course provider to correct the Step 3 questionnaire and submit it again.", color: 'yellow' }
          : { text: "The Step 3 questionnaire needs some correction. Please correct it and submit it again.", color: 'blue' }

      case `${STEPS.REVIEW} | expert`:
        if (course.expert) {
          return roles.isExternalExpert
            ? { text: `Please review this course and submit your recommendations.`, color: 'blue' }
            : { text: `Waiting for the invited external reviewer ${WorkflowTextService.formatExpertLink(course.expert, roles)} to review this course.`, color: 'yellow' };
        } else {
          return roles.isStaff
            ? { text: `Please assign an external export for this course.`, color: 'blue' }
            : { text: `Waiting for an external reviewer to be assigned to review this course.`, color: 'yellow' }
        }

      case `${STEPS.REVIEW} | committee`:
        if (roles.isStaff) {
          return { text: "Please finalize the submission.", color: 'blue' }
        } else {
          return { text: "Waiting for the EIT UM staff to finalize the submission.", color: 'yellow' }
        }
      case `COMPLETED`:
        if (course?.LabelAwarded === AWARD_STATUS.AWARDED) {
          return { text: `This submission has been completed. The EIT UM Label has been awarded.`, color: 'gray' }
        } else if (course?.LabelAwarded === AWARD_STATUS.NOT_AWARDED) {
          return { text: `This submission has been completed. The EIT UM Label has NOT been awarded.`, color: 'gray' }
        } else {
          return { text: `This submission has been completed.`, color: 'gray' }
        }
    };
  }

  public static isAssignToExpertEnabled(course: ICourse, roles: ICourseRoles) {
    return course.CourseStep === `${STEPS.REVIEW} | expert` && !!roles.isStaff;
  }

  public static getNextModule(moduleId: string | undefined) {
    switch (moduleId) {
      case STEPS.STEP1:
        return STEPS.STEP2;
      case STEPS.STEP2:
        return STEPS.STEP3;
      case STEPS.STEP3:
        return STEPS.REVIEW;
    }
  }

  public static getResultSources(
    course: ICourse, 
    module: ICourseModule, 
    results_template: any,
    provider?: IProvider,
  ): IResultsSource[] {

    const courseId = course.id;
    const moduleId = module.id;
    const mapping = WorkflowTextService.getDefaultMapping(results_template, [/^Approval/, /^Submit/, /^Feedback/, /^Award/, /^Expert/, /Date$/]);

    const sources: IResultsSource[] = [];
    sources.push({ data: WorkflowTextService.getDefaultResults(results_template), mapping });

    if (provider) {
      sources.push({ data: provider, mapping });
    }

    if (course.coursePreviousRunId) {
      const previousRunMapping = WorkflowTextService.getDefaultMapping(results_template, [/^Approval/, /^Submit/, /^Feedback/, /^Award/, /^Expert/, /Date$/, /^File/]);
      sources.push({ courseId: course.coursePreviousRunId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping: previousRunMapping });
      sources.push({ courseId: course.coursePreviousRunId, moduleId: STEPS.STEP2, fileName: FILENAMES.RESULTS, mapping: previousRunMapping });
      sources.push({ courseId: course.coursePreviousRunId, moduleId: STEPS.STEP3, fileName: FILENAMES.RESULTS, mapping: previousRunMapping });
    }

    if (moduleId === STEPS.STEP2) {
      sources.push({ courseId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping });
    }

    if (moduleId === STEPS.STEP3) {
      sources.push({ courseId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping });
      sources.push({ courseId, moduleId: STEPS.STEP2, fileName: FILENAMES.RESULTS, mapping });
    }

    if (moduleId === STEPS.REVIEW) {
      sources.push({ courseId, moduleId: STEPS.STEP1, fileName: FILENAMES.RESULTS, mapping });
      sources.push({ courseId, moduleId: STEPS.STEP2, fileName: FILENAMES.RESULTS, mapping });
      sources.push({ courseId, moduleId: STEPS.STEP3, fileName: FILENAMES.RESULTS, mapping });
    }

    return sources;
  }

  public static getDraftStatus(courseStatus: string | undefined): string {
    switch (courseStatus) {
      case `${STEPS.STEP1} | rejected`: return `${STEPS.STEP1} | draft`;
      case `${STEPS.STEP2} | rejected`: return `${STEPS.STEP2} | draft`;
      case `${STEPS.STEP3} | rejected`: return `${STEPS.STEP3} | draft`;
      case `${STEPS.STEP1} | approved`: return `${STEPS.STEP2} | draft`;
      case `${STEPS.STEP2} | approved`: return `${STEPS.STEP3} | draft`;
      default: return '';
    }
  }

  /**
   * V1 workflows don't use calculated deadlines.
   * This stub exists to avoid platform version checks in shared code.
   */
  public static getCurrentDeadline(_courseStep: string | undefined, _deadlines: any, _hasReviewResults: boolean): undefined {
    return undefined;
  }

  public static getFeedbackSources() {
    return []
  }
}